import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router";
import config from "../../app-config";
import { fetch_audio_by_id, create_new_audio, update_audio_by_id, delete_audio_by_id } from "../../api/audioAPI";
import "react-toastify/dist/ReactToastify.css";
import AdminLayout from "../../layouts/AdminLayout";
import { MdAdd, MdArrowBack, MdSave, MdDelete } from "react-icons/md";
import FileUploadField from "../../components/FileUploadField";
import notify from "../../utils/IANotification";
import SubNavbar from "../../components/ui/SubNavbar";
import Card from "../../components/ui/Card";
import Button from "../../components/ui/Button";

export default function AudioFormPage() {
  const navigate = useNavigate();
  let audio_id = new URLSearchParams(window.location.search).get("audio_id");

  const [activeTab, setActiveTab] = useState("general");
  const [audioData, setAudioData] = useState({
    title: "",
    remarks: "",
    metadata: [],
    is_published: 0,
    audio_category_id: "",
  })

  const tabs = [
    { id: 'general', label: 'Audio Information' },
    { id: 'metadata', label: 'Metadata Tags' },
    { id: 'file', label: 'Audio Media File' },
  ]

  useEffect(() => {
    fetchAudioById();
  }, []);

  const fetchAudioById = async () => {
    if (audio_id === "new" || !audio_id) return;
    const data = await fetch_audio_by_id(audio_id);
    if (data?.status === 200) {
      setAudioData({
        ...data.data,
        metadata: data.data.metadata || []
      });
    }
  }

  const createAudio = async () => {
    const data = await create_new_audio(audioData);
    if (data?.status === 200) {
      notify({ message: "Audio record created successfully", type: "success" });
      navigate(`/admin/audio/details?audio_id=${data.data.id}`);
    } else {
      notify({ message: data?.message, type: "error" });
    }
  }

  const saveAudio = async () => {
    if (audio_id === "new" || !audio_id) return;
    const data = await update_audio_by_id(audio_id, audioData);
    if (data?.status === 200) {
      notify({ message: "Audio saved successfully", type: "success" });
      fetchAudioById();
    } else {
      notify({ message: data?.message, type: "error" });
    }
  }

  const deleteAudio = async () => {
    if (audio_id === "new" || !audio_id) return;
    const data = await delete_audio_by_id(audio_id);
    if (data?.status === 200) {
      notify({ message: "Audio deleted successfully", type: "success" });
      navigate("/admin/audio");
    } else {
      notify({ message: data?.message, type: "error" });
    }
  }

  return (
    <AdminLayout
      navSelected={"Audio & Worship"}
      headerText={audio_id === "new" ? "New Audio Record" : "Edit Audio Record"}
      toolbar={
        <>
          <div className="flex items-center gap-3">
            <Button variant="outline" icon={MdArrowBack} onClick={() => navigate('/admin/audio')}>
              Back
            </Button>
            <h1 className='text-xl font-bold text-gray-900'>
              {audio_id === "new" ? "Create Audio Record" : audioData.title || "Edit Audio Record"}
            </h1>
          </div>
          <div className='flex gap-2'>
            {audio_id !== "new" && (
              <Button variant="danger" icon={MdDelete} onClick={deleteAudio}>
                Delete
              </Button>
            )}
            <Button variant="primary" icon={MdSave} onClick={() => {
              if (audio_id === "new") createAudio();
              else saveAudio();
            }}>
              {audio_id === "new" ? "Create" : "Save Changes"}
            </Button>
          </div>
        </>
      }
    >
      <SubNavbar
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
      />

      {/* TAB 1: GENERAL INFO */}
      {activeTab === "general" && (
        <Card title="Audio Details" subtitle="Title and general remarks.">
          <div className="space-y-6">
            <div className="input-container">
              <label className="input-label font-semibold">Audio Track Title</label>
              <input
                type="text"
                className="text-input"
                placeholder="e.g. Sunday Worship Set (24 Mar)"
                value={audioData.title || ""}
                onChange={(e) => setAudioData({ ...audioData, title: e.target.value })}
              />
            </div>

            <div className="input-container">
              <label className="input-label font-semibold">Remarks &amp; Notes</label>
              <textarea
                className="textarea-input h-28"
                placeholder="Internal notes or recording description..."
                value={audioData.remarks || ""}
                onChange={(e) => setAudioData({ ...audioData, remarks: e.target.value })}
              />
            </div>
          </div>
        </Card>
      )}

      {/* TAB 2: METADATA */}
      {activeTab === "metadata" && (
        <Card
          title="Audio Track Metadata"
          subtitle="Custom key-value attributes (e.g. Artist, Album, Year)."
          headerRight={
            <Button
              variant="secondary"
              size="sm"
              icon={MdAdd}
              onClick={() => {
                let newMetadata = [...(audioData.metadata || [])];
                newMetadata.push({ key: "", value: "", label: "" });
                setAudioData({ ...audioData, metadata: newMetadata });
              }}
            >
              Add Attribute
            </Button>
          }
        >
          {audioData?.metadata?.length > 0 ? (
            <div className="space-y-3">
              {audioData.metadata.map((item, index) => (
                <div key={index} className="p-4 bg-gray-50 rounded-xl border border-gray-200 grid grid-cols-1 sm:grid-cols-3 gap-4 items-end">
                  <div className="input-container">
                    <label className="input-label font-semibold">Key Identifier</label>
                    <input
                      type="text"
                      className="text-input mb-0"
                      placeholder="artist"
                      value={item.key || ""}
                      onChange={(e) => {
                        let newMetadata = [...audioData.metadata];
                        newMetadata[index].key = e.target.value?.toLowerCase();
                        setAudioData({ ...audioData, metadata: newMetadata });
                      }}
                    />
                  </div>

                  <div className="input-container">
                    <label className="input-label font-semibold">Display Label</label>
                    <input
                      type="text"
                      className="text-input mb-0"
                      placeholder="Artist Name"
                      value={item.label || ""}
                      onChange={(e) => {
                        let newMetadata = [...audioData.metadata];
                        newMetadata[index].label = e.target.value;
                        setAudioData({ ...audioData, metadata: newMetadata });
                      }}
                    />
                  </div>

                  <div className="input-container">
                    <label className="input-label font-semibold">Value</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        className="text-input mb-0 flex-grow"
                        placeholder="John Tan"
                        value={item.value || ""}
                        onChange={(e) => {
                          let newMetadata = [...audioData.metadata];
                          newMetadata[index].value = e.target.value;
                          setAudioData({ ...audioData, metadata: newMetadata });
                        }}
                      />
                      <Button
                        variant="danger"
                        size="sm"
                        icon={MdDelete}
                        onClick={() => {
                          let newMetadata = [...audioData.metadata];
                          newMetadata.splice(index, 1);
                          setAudioData({ ...audioData, metadata: newMetadata });
                        }}
                      >
                        Remove
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-gray-400 italic py-6 text-center">No metadata attributes added yet.</p>
          )}
        </Card>
      )}

      {/* TAB 3: FILE ATTACHMENT */}
      {activeTab === "file" && (
        <Card title="Audio Media File" subtitle="Upload MP3/WAV audio track and preview playback.">
          {audio_id === "new" ? (
            <p className="text-xs text-gray-400 italic py-6 text-center">Save the audio record first before uploading audio files.</p>
          ) : (
            <div className="space-y-6">
              <FileUploadField
                max_file_size={1000000}
                max_files={1}
                item_index={0}
                upload_url={config.endpoint + "/file/audio/id/" + audio_id}
                text="Upload Audio File"
                subtext="Upload MP3/WAV recording for worship sessions or sermons"
                onChange={() => fetchAudioById()}
              />

              {audioData?.file_data?.ext_file_path && (
                <div className="p-4 bg-gray-50 rounded-xl border border-gray-200">
                  <label className="input-label font-semibold block mb-2">Media Playback Preview</label>
                  <audio controls className="w-full" src={`${config.endpoint}${audioData?.file_data?.ext_file_path}`} />
                </div>
              )}
            </div>
          )}
        </Card>
      )}
    </AdminLayout>
  );
}